import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import {
  fetchClientDropDownList,
  fetchEntityDropDownList,
  fetchPermissions,
  fetchPlatformPermissions,
  fetchRolesAndPermissions,
  savePlatformRolePermissions,
  saveRolePermissions,
} from "../../services/api";
import styles from "./ManageRolesPermissions.module.css";
import { InfoCircleFill } from "react-bootstrap-icons";
import { CUSTOMIZATION_TYPES, USER_ROLES } from "../../lib/utils/constant";
import ViewOnlyWrapper from "../ViewOnlyWrapper";
import UserGuideRolePermissions from "./UserGuideRolePermissions";

const EMPTY_PERMISSION_RESPONSE = {
  assigned: [],
  available: [],
  dependencies: {},
};

const HIDDEN_PERMISSION_CODES = new Set([
  "rbac:manage",
  "rbac:read",
  "audit:track",
]);

const normalizePermission = (permission) => {
  const permissionId =
    permission?.id ?? permission?.permissionId ?? permission?.value;
  const code =
    permission?.code ??
    permission?.permissionCode ??
    permission?.key ??
    permission?.id;
  if (!code) return null;
  const codeAsString = String(code);
  const name =
    permission?.name ??
    permission?.label ??
    permission?.permissionName ??
    codeAsString;
  const module = String(
    permission?.module ?? permission?.resource ?? "system",
  ).trim();
  const isLockedValue =
    permission?.islocked ?? permission?.isLocked ?? permission?.locked;
  const isLocked =
    isLockedValue === true ||
    String(isLockedValue || "").trim().toLowerCase() === "true";
  return {
    id: permissionId != null ? Number(permissionId) : null,
    code: codeAsString,
    name: String(name),
    module: String(module),
    isLocked,
  };
};

const formatModuleTitle = (moduleName) =>
  String(moduleName || "")
    .replace(/_/g, " ")
    .trim();

const titleCase = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .trim();

const OPERATION_LABELS = {
  read: "View",
  view: "View",
  create: "Create",
  update: "Update",
  delete: "Delete",
  export: "Export",
  cancel: "Cancel",
  approve: "Approve",
  assign: "Assign",
  manage: "Manage",
  manage_client: "Manage Payment for Client",
};

const normalizeActionToken = (rawAction) => {
  const normalizedAction = String(rawAction || "")
    .toLowerCase()
    .trim();
  if (!normalizedAction) return "";

  // Permission codes like "settings_update" should display only the action.
  if (normalizedAction.startsWith("settings_")) {
    const strippedAction = normalizedAction.replace(/^settings_/, "");
    if (strippedAction) return strippedAction;
  }

  return normalizedAction;
};

const derivePermissionAction = (permission) => {
  const actionFromCode = String(permission?.code || "")
    .split(":")
    .pop()
    ?.trim();
  if (actionFromCode) {
    const normalized = normalizeActionToken(actionFromCode);
    return OPERATION_LABELS[normalized] || titleCase(normalized);
  }

  const actionFromName = String(permission?.name || "")
    .trim()
    .match(
      /\b(read|view|create|update|delete|export|cancel|approve|assign|manage)\b$/i,
    )?.[1];

  if (actionFromName) {
    const normalized = normalizeActionToken(actionFromName);
    return OPERATION_LABELS[normalized] || titleCase(normalized);
  }

  return "Access";
};

const deriveSubgroupLabel = (permission) => {
  const actionLabel = derivePermissionAction(permission);
  const actionPattern = new RegExp(`\\s*${actionLabel}\\s*$`, "i");
  const cleanedName = String(permission?.name || "")
    .replace(/\s+permissions?$/i, "")
    .replace(actionPattern, "")
    .trim();

  if (cleanedName) return cleanedName;

  const resourceFromCode = String(permission?.code || "")
    .split(":")
    .slice(0, -1)
    .join(":")
    .split(".")
    .pop()
    .split("_")
    .join(" ")
    .trim();

  if (resourceFromCode) return titleCase(resourceFromCode);

  return titleCase(permission?.module || "General");
};

const getPermissionDisplayLabel = (permission) => {
  if (!permission) return "";
  const actionLabel = derivePermissionAction(permission);
  const subgroupLabel = deriveSubgroupLabel(permission);
  if (!subgroupLabel) return actionLabel;
  return `${actionLabel} (${subgroupLabel})`;
};

const extractPermissionItems = (response) => {
  const payload = response?.data?.data ?? response?.data ?? {};
  const assignedSource =
    payload?.assigned ??
    payload?.assignedPermissions ??
    payload?.selected ??
    payload?.permissions ??
    [];
  const availableSource =
    payload?.available ?? payload?.availablePermissions ?? [];
  const dependencies = payload?.dependencies ?? {};

  const assigned = (Array.isArray(assignedSource) ? assignedSource : [])
    .map(normalizePermission)
    .filter(Boolean);
  const available = (Array.isArray(availableSource) ? availableSource : [])
    .map(normalizePermission)
    .filter(Boolean);

  return {
    assigned,
    available,
    dependencies,
  };
};

const MODULE_ICONS = {
  order: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2 3.2A1.2 1.2 0 0 1 3.2 2h9.6A1.2 1.2 0 0 1 14 3.2v9.6a1.2 1.2 0 0 1-1.2 1.2H3.2A1.2 1.2 0 0 1 2 12.8V3.2Zm1 0V5h10V3.2a.2.2 0 0 0-.2-.2H3.2a.2.2 0 0 0-.2.2Zm10 2.8H3v6.8c0 .11.09.2.2.2h9.6a.2.2 0 0 0 .2-.2V6Z" />
    </svg>
  ),
  payment: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M1.5 4A1.5 1.5 0 0 1 3 2.5h10A1.5 1.5 0 0 1 14.5 4v8A1.5 1.5 0 0 1 13 13.5H3A1.5 1.5 0 0 1 1.5 12V4ZM3 3.5a.5.5 0 0 0-.5.5v1h11V4a.5.5 0 0 0-.5-.5H3Zm10.5 3h-11V12a.5.5 0 0 0 .5.5h10a.5.5 0 0 0 .5-.5V6.5Z" />
    </svg>
  ),
  receipt: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M3 1.5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1V14l-2-1-2 1-2-1-2 1-2-1V1.5Zm1 0v10.9l1 .5 2-1 2 1 2-1 1 .5V1.5H4Zm1.5 2h5v1h-5v-1Zm0 2h5v1h-5v-1Zm0 2h3.5v1H5.5v-1Z" />
    </svg>
  ),
  subscription: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 1a7 7 0 1 0 7 7A7 7 0 0 0 8 1Zm0 1a6 6 0 0 1 4.98 9.35L6.65 5.02A5.97 5.97 0 0 1 8 2Zm0 12a5.97 5.97 0 0 1-4.98-9.35l6.33 6.33A5.97 5.97 0 0 1 8 14Z" />
    </svg>
  ),
  customer: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M10 5a2 2 0 1 1-4 0 2 2 0 0 1 4 0Zm-2 3c-2.76 0-5 1.34-5 3v1h10v-1c0-1.66-2.24-3-5-3Z" />
    </svg>
  ),
  client: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M5.5 4a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0Zm8 0a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0ZM4 7c-1.66 0-3 1-3 2.25V10h4.5V9.25C5.5 8 5.16 7 4 7Zm8 0c-1.16 0-1.5 1-1.5 2.25V10H15V9.25C15 8 13.66 7 12 7Z" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 8a3 3 0 1 0-3-3 3 3 0 0 0 3 3Zm0 1c-2.67 0-5 1.34-5 3v1h10v-1c0-1.66-2.33-3-5-3Z" />
    </svg>
  ),
  device: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 1.5A1.5 1.5 0 0 1 5.5 0h5A1.5 1.5 0 0 1 12 1.5v13a1.5 1.5 0 0 1-1.5 1.5h-5A1.5 1.5 0 0 1 4 14.5v-13Zm1.5-.5a.5.5 0 0 0-.5.5v13a.5.5 0 0 0 .5.5h5a.5.5 0 0 0 .5-.5v-13a.5.5 0 0 0-.5-.5h-5Z" />
    </svg>
  ),
  sim: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 1h5l3 3v11H4V1Zm1 1v12h6V4.5L8.5 2H5Zm1.2 4h3.6v1H6.2V6Zm0 2h3.6v1H6.2V8Zm0 2h2.4v1H6.2v-1Z" />
    </svg>
  ),
  alerts: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 1.5a4 4 0 0 0-4 4v2.3L2.5 10v1h11v-1L12 7.8V5.5a4 4 0 0 0-4-4ZM6 12a2 2 0 0 0 4 0H6Z" />
    </svg>
  ),
  report: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M2 2h12v12H2V2Zm1 1v10h10V3H3Zm1 7h1.5V6H4v4Zm3 0h1.5V4H7v6Zm3 0h1.5V8H10v2Z" />
    </svg>
  ),
  rbac: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M8 0 2 2.5v4.9c0 3.3 2.1 6.3 5.3 7.8L8 15.5l.7-.3c3.2-1.5 5.3-4.5 5.3-7.8V2.5L8 0Zm0 1.1 5 2.1v4.2c0 2.8-1.8 5.4-4.5 6.7L8 14.3l-.5-.2C4.8 12.8 3 10.2 3 7.4V3.2l5-2.1Zm0 2.4a1.7 1.7 0 1 0 0 3.4 1.7 1.7 0 0 0 0-3.4Zm0 4.3c-1.8 0-3.3.9-3.3 2V11h6.6V9.8c0-1.1-1.5-2-3.3-2Z" />
    </svg>
  ),
  system: (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path d="M9.6 1.1a1 1 0 0 0-1.2 0l-.4.3a1 1 0 0 1-1 .1l-.4-.2a1 1 0 0 0-1.2.5l-.2.5a1 1 0 0 1-.8.7l-.5.1a1 1 0 0 0-.8.9v.6a1 1 0 0 1-.5.9l-.4.3a1 1 0 0 0-.2 1.3l.3.4a1 1 0 0 1 .1 1l-.2.4a1 1 0 0 0 .5 1.2l.5.2a1 1 0 0 1 .7.8l.1.5a1 1 0 0 0 .9.8h.6a1 1 0 0 1 .9.5l.3.4a1 1 0 0 0 1.3.2l.4-.3a1 1 0 0 1 1-.1l.4.2a1 1 0 0 0 1.2-.5l.2-.5a1 1 0 0 1 .8-.7l.5-.1a1 1 0 0 0 .8-.9v-.6a1 1 0 0 1 .5-.9l.4-.3a1 1 0 0 0 .2-1.3l-.3-.4a1 1 0 0 1-.1-1l.2-.4a1 1 0 0 0-.5-1.2l-.5-.2a1 1 0 0 1-.7-.8l-.1-.5a1 1 0 0 0-.9-.8h-.6a1 1 0 0 1-.9-.5l-.3-.4ZM8 5.6A2.4 2.4 0 1 1 8 10.4 2.4 2.4 0 0 1 8 5.6Z" />
    </svg>
  ),
};

function ManageRolesPermissions({ show, onHide, canManageRoles }) {
  const currentUser = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch {
      return {};
    }
  }, []);
  const roleCode = currentUser?.role?.code || currentUser?.roleCode || "";
  const accountType = currentUser?.entityMaster?.accountType;
  const normalizedAccountType = String(accountType || "").toUpperCase();
  const isEntityAccount = normalizedAccountType === USER_ROLES.ENTITY;
  const isClientAccount = normalizedAccountType === USER_ROLES.CLIENT;
  const currentEntityMasterId =
    currentUser?.entityMaster?.id ??
    currentUser?.entityMasterId ??
    currentUser?.entityId ??
    "";
  const currentParentEntityId =
    currentUser?.entityMaster?.parent_id ??
    currentUser?.parentEntityMasterId ??
    currentUser?.parentEntityId ??
    "";
  const isSystemAdmin = roleCode === USER_ROLES.SYS_ADMIN;

  const [scope, setScope] = useState(
    isSystemAdmin ? "platform" : isClientAccount ? "client" : "entity",
  );
  const [selectedEntity, setSelectedEntity] = useState("");
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [entityOptions, setEntityOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isEntityLoading, setIsEntityLoading] = useState(false);
  const [isClientLoading, setIsClientLoading] = useState(false);
  const [isRoleLoading, setIsRoleLoading] = useState(false);
  const [isPermissionLoading, setIsPermissionLoading] = useState(false);
  const [isSavingPermissions, setIsSavingPermissions] = useState(false);
  const [permissionResponse, setPermissionResponse] = useState(
    EMPTY_PERMISSION_RESPONSE,
  );
  const [dependencyModal, setDependencyModal] = useState({
    show: false,
    parentLabel: "",
    dependencyLabel: "",
  });
  const [saveResponseModal, setSaveResponseModal] = useState({
    show: false,
    type: "success",
    title: "",
    message: "",
  });
  const [permissionSearch, setPermissionSearch] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [openCategories, setOpenCategories] = useState({});
  const [showUserGuide, setShowUserGuide] = useState(false);
  const isViewOnly =
    Number(selectedRole) === 4 && roleCode === USER_ROLES.ADMIN;
  const extractDropdownItems = (response) => {
    const payload = response?.data?.data;
    const rawItems = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(response?.data?.items)
          ? response.data.items
          : Array.isArray(response?.data)
            ? response.data
            : [];

    return rawItems
      .map((item) => {
        const value =
          item?.id ??
          item?.value ??
          item?.entityId ??
          item?.clientId ??
          item?._id;
        const label = item?.name ?? item?.label ?? item?.title;
        if (value == null || !label) return null;
        return { value: String(value), label: String(label) };
      })
      .filter(Boolean);
  };

  const extractRoleItems = (response) => {
    const payload = response?.data?.data;
    const rawItems = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(response?.data?.items)
          ? response.data.items
          : Array.isArray(response?.data)
            ? response.data
            : [];

    const roleItems = rawItems
      .map((item) => {
        const value = item?.id ?? item?.value ?? item?.roleId ?? item?._id;
        const label =
          item?.name ??
          item?.label ??
          item?.title ??
          item?.role ??
          item?.roleName;
        if (value == null || !label) return null;
        return { value: String(value), label: String(label).trim() };
      })
      .filter(Boolean);

    return roleItems.filter(
      (role, index, arr) =>
        arr.findIndex((candidate) => candidate.value === role.value) === index,
    );
  };

  const handleScopeChange = (nextScope) => {
    setScope(nextScope);
    setSelectedRole("");
    setSelectedClient("");
  };

  const handleEntityChange = (event) => {
    const value = event.target.value;
    setSelectedEntity(value);
    setSelectedClient("");
    setClientOptions([]);
    setSelectedRole("");
    if (value) {
      void loadClientOptions(value);
    }
  };

  const loadEntityOptions = useCallback(async () => {
    setIsEntityLoading(true);
    try {
      const response = await fetchEntityDropDownList();
      setEntityOptions(extractDropdownItems(response));
    } catch (error) {
      console.error("Failed to fetch entity dropdown list:", error);
      setEntityOptions([]);
    } finally {
      setIsEntityLoading(false);
    }
  }, []);

  const loadClientOptions = useCallback(async (entityId) => {
    if (!entityId) {
      setClientOptions([]);
      return;
    }
    setIsClientLoading(true);
    try {
      const response = await fetchClientDropDownList(entityId);
      setClientOptions(extractDropdownItems(response));
    } catch (error) {
      console.error("Failed to fetch client dropdown list:", error);
      setClientOptions([]);
    } finally {
      setIsClientLoading(false);
    }
  }, []);

  const loadRoleOptions = useCallback(async (customizationType) => {
    setIsRoleLoading(true);
    try {
      const response = await fetchRolesAndPermissions(customizationType);
      setRoleOptions(extractRoleItems(response));
    } catch (error) {
      console.error("Failed to fetch role dropdown list:", error);
      setRoleOptions([]);
    } finally {
      setIsRoleLoading(false);
    }
  }, []);

  const isRoleDisabled =
    scope === "platform"
      ? roleOptions.length === 0
      : scope === "client"
        ? !selectedClient || roleOptions.length === 0
        : !selectedEntity || roleOptions.length === 0;

  const targetIdForScope = useMemo(() => {
    if (scope === "client") return selectedClient;
    if (scope === "platform") {
      return currentEntityMasterId != null
        ? String(currentEntityMasterId)
        : selectedEntity;
    }
    return selectedEntity;
  }, [scope, selectedClient, currentEntityMasterId, selectedEntity]);

  useEffect(() => {
    setSelectedRole("");
  }, [scope, selectedEntity, selectedClient]);

  useEffect(() => {
    if (show) {
      void loadEntityOptions();
    }
  }, [show, loadEntityOptions]);

  useEffect(() => {
    if (!show) return;
    if (isSystemAdmin) {
      setScope("platform");
      return;
    }

    if (isClientAccount) {
      setScope("client");
      setSelectedEntity(
        currentParentEntityId != null ? String(currentParentEntityId) : "",
      );
      setSelectedClient(
        currentEntityMasterId != null ? String(currentEntityMasterId) : "",
      );
      return;
    }

    if (isEntityAccount) {
      setScope("entity");
      setSelectedEntity(
        currentEntityMasterId != null ? String(currentEntityMasterId) : "",
      );
      setSelectedClient("");
    }
  }, [
    show,
    isSystemAdmin,
    isClientAccount,
    isEntityAccount,
    currentParentEntityId,
    currentEntityMasterId,
  ]);

  useEffect(() => {
    if (!show || !selectedEntity) return;
    if (isEntityAccount || isClientAccount) {
      void loadClientOptions(selectedEntity);
    }
  }, [
    show,
    selectedEntity,
    isEntityAccount,
    isClientAccount,
    loadClientOptions,
  ]);

  useEffect(() => {
    if (!show) return;
    const customizationType =
      scope === "platform"
        ? CUSTOMIZATION_TYPES.GLOBAL
        : CUSTOMIZATION_TYPES.NON_GLOBAL;
    void loadRoleOptions(customizationType);
  }, [show, scope, loadRoleOptions]);

  useEffect(() => {
    if (!show || !selectedRole) {
      setPermissionResponse(EMPTY_PERMISSION_RESPONSE);
      return;
    }

    if (scope !== "platform" && !targetIdForScope) {
      setPermissionResponse(EMPTY_PERMISSION_RESPONSE);
      return;
    }

    let isMounted = true;
    const loadPermissions = async () => {
      setIsPermissionLoading(true);
      try {
        const response =
          scope === "platform"
            ? await fetchPlatformPermissions(selectedRole)
            : await fetchPermissions(targetIdForScope, selectedRole);
        if (isMounted) {
          setPermissionResponse(extractPermissionItems(response));
        }
      } catch (error) {
        console.error("Failed to fetch permissions:", error);
        if (isMounted) {
          setPermissionResponse(EMPTY_PERMISSION_RESPONSE);
        }
      } finally {
        if (isMounted) {
          setIsPermissionLoading(false);
        }
      }
    };

    void loadPermissions();

    return () => {
      isMounted = false;
    };
  }, [show, selectedRole, scope, targetIdForScope]);

  const allPermissions = useMemo(() => {
    const merged = [
      ...(permissionResponse.assigned || []),
      ...(permissionResponse.available || []),
    ];
    const unique = [];
    const seen = new Set();
    merged.forEach((perm) => {
      if (!perm?.code || seen.has(perm.code)) return;
      seen.add(perm.code);
      unique.push(perm);
    });
    return unique;
  }, [permissionResponse]);

  const permissionByCode = useMemo(() => {
    const map = {};
    allPermissions.forEach((perm) => {
      map[perm.code] = perm;
    });
    return map;
  }, [allPermissions]);

  const lockedAssignedCodes = useMemo(
    () =>
      new Set(
        (permissionResponse.assigned || [])
          .filter((perm) => perm?.isLocked)
          .map((perm) => perm.code),
      ),
    [permissionResponse],
  );

  const lockedAvailableCodes = useMemo(
    () =>
      new Set(
        (permissionResponse.available || [])
          .filter((perm) => perm?.isLocked)
          .map((perm) => perm.code),
      ),
    [permissionResponse],
  );

  const lockedPermissionCodes = useMemo(() => {
    const codes = new Set();
    lockedAssignedCodes.forEach((code) => codes.add(code));
    lockedAvailableCodes.forEach((code) => codes.add(code));
    return codes;
  }, [lockedAssignedCodes, lockedAvailableCodes]);

  const dependencies = useMemo(
    () => permissionResponse.dependencies || {},
    [permissionResponse],
  );

  const permissionsByModule = useMemo(() => {
    const map = {};
    allPermissions.forEach((perm) => {
      if (HIDDEN_PERMISSION_CODES.has(perm.code)) return;
      if (!map[perm.module]) map[perm.module] = [];
      map[perm.module].push(perm);
    });
    Object.keys(map).forEach((moduleKey) => {
      map[moduleKey].sort((a, b) => a.name.localeCompare(b.name));
    });
    return map;
  }, [allPermissions]);

  const visibleAssignedCount = useMemo(
    () =>
      selectedPermissions.filter(
        (code) =>
          !HIDDEN_PERMISSION_CODES.has(code) && permissionByCode[code] != null,
      ).length,
    [selectedPermissions, permissionByCode],
  );

  const visibleTotalPermissions = useMemo(
    () =>
      allPermissions.filter((perm) => !HIDDEN_PERMISSION_CODES.has(perm.code))
        .length,
    [allPermissions],
  );

  const filteredCategories = useMemo(() => {
    const term = permissionSearch.trim().toLowerCase();
    return Object.entries(permissionsByModule)
      .map(([moduleKey, modulePermissions]) => {
        const filteredPermissions = !term
          ? modulePermissions
          : modulePermissions.filter((perm) =>
              [perm.name, perm.code, perm.module].some((field) =>
                String(field || "")
                  .toLowerCase()
                  .includes(term),
              ),
            );
        if (filteredPermissions.length === 0) return null;

        const subgroupMap = {};
        filteredPermissions.forEach((perm) => {
          const subgroupLabel = deriveSubgroupLabel(perm);
          const subgroupKey = subgroupLabel.toLowerCase().replace(/\s+/g, "_");
          if (!subgroupMap[subgroupKey]) {
            subgroupMap[subgroupKey] = {
              key: `${moduleKey}:${subgroupKey}`,
              moduleKey,
              moduleLabel: subgroupLabel,
              permissions: [],
            };
          }
          subgroupMap[subgroupKey].permissions.push(perm);
        });

        const subgroups = Object.values(subgroupMap)
          .map((subgroup) => ({
            ...subgroup,
            permissions: subgroup.permissions.sort((a, b) => {
              const actionKeyA =
                String(a?.code || "")
                  .split(":")
                  .pop()
                  ?.trim()
                  .toLowerCase() || "";
              const actionKeyB =
                String(b?.code || "")
                  .split(":")
                  .pop()
                  ?.trim()
                  .toLowerCase() || "";
              if (actionKeyA && actionKeyB)
                return actionKeyA.localeCompare(actionKeyB);
              if (actionKeyA) return -1;
              if (actionKeyB) return 1;
              return derivePermissionAction(a).localeCompare(
                derivePermissionAction(b),
              );
            }),
          }))
          .sort((a, b) => a.moduleLabel.localeCompare(b.moduleLabel));

        return {
          key: moduleKey,
          title: formatModuleTitle(moduleKey),
          moduleKey,
          subgroups,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [permissionSearch, permissionsByModule]);

  const getDependentParents = useCallback(
    (dependencyCode, selectionSet, excludedParents = new Set()) => {
      return Object.entries(dependencies).reduce(
        (parents, [parentCode, dependencyCodes]) => {
          if (HIDDEN_PERMISSION_CODES.has(parentCode)) {
            return parents;
          }

          if (!permissionByCode[parentCode]) {
            return parents;
          }

          if (
            Array.isArray(dependencyCodes) &&
            dependencyCodes.includes(dependencyCode) &&
            selectionSet.has(parentCode) &&
            !excludedParents.has(parentCode)
          ) {
            parents.push(parentCode);
          }
          return parents;
        },
        [],
      );
    },
    [dependencies, permissionByCode],
  );

  const openDependencyModal = useCallback(
    (dependencyCode, parentCodes) => {
      const dependencyPermission = permissionByCode[dependencyCode];
      const dependencyLabel =
        getPermissionDisplayLabel(dependencyPermission) || dependencyCode;
      const parentLabels = parentCodes
        .map((code) => {
          const parentPermission = permissionByCode[code];
          return getPermissionDisplayLabel(parentPermission) || code;
        })
        .join(", ");

      setDependencyModal({
        show: true,
        parentLabel: parentLabels,
        dependencyLabel,
      });
    },
    [permissionByCode],
  );

  const closeDependencyModal = () => {
    setDependencyModal({ show: false, parentLabel: "", dependencyLabel: "" });
  };

  const closeSaveResponseModal = () => {
    setSaveResponseModal({
      show: false,
      type: "success",
      title: "",
      message: "",
    });
  };

  const enforceLockedPermissionState = useCallback(
    (codesSet) => {
      const next = new Set(codesSet);
      lockedAssignedCodes.forEach((code) => next.add(code));
      lockedAvailableCodes.forEach((code) => next.delete(code));
      return next;
    },
    [lockedAssignedCodes, lockedAvailableCodes],
  );

  const buildNextSelection = (
    currentSelection,
    code,
    checked,
    excludedParents = new Set(),
  ) => {
    const next = new Set(currentSelection);
    const permission = permissionByCode[code];
    if (!permission || lockedPermissionCodes.has(code)) {
      return enforceLockedPermissionState(next);
    }

    if (checked) {
      next.add(code);
      (dependencies[code] || []).forEach((depCode) => next.add(depCode));
    } else {
      const blockingParents = getDependentParents(code, next, excludedParents);
      if (blockingParents.length > 0) {
        openDependencyModal(code, blockingParents);
        return next;
      }
      next.delete(code);
    }

    return enforceLockedPermissionState(next);
  };

  const handlePermissionToggle = (code, checked) => {
    const next = buildNextSelection(
      new Set(selectedPermissions),
      code,
      checked,
    );
    setSelectedPermissions(Array.from(next));
  };

  const handleBulkToggle = (permissionCodes, checked) => {
    if (!permissionCodes.length) return;
    let working = new Set(selectedPermissions);
    const excludedParents = checked ? new Set() : new Set(permissionCodes);
    permissionCodes.forEach((code) => {
      working = buildNextSelection(working, code, checked, excludedParents);
    });
    setSelectedPermissions(Array.from(working));
  };

  const getCheckedStats = (codes) => {
    const total = codes.length;
    const checkedCount = codes.filter((code) =>
      selectedPermissions.includes(code),
    ).length;
    return { total, checkedCount };
  };

  useEffect(() => {
    if (!selectedRole) {
      setSelectedPermissions([]);
      setPermissionSearch("");
      return;
    }
    const defaults = new Set(
      (permissionResponse.assigned || []).map((perm) => perm.code),
    );
    setSelectedPermissions(Array.from(enforceLockedPermissionState(defaults)));
  }, [selectedRole, permissionResponse, enforceLockedPermissionState]);

  const toggleCategory = (categoryKey) => {
    setOpenCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const handleSaveChanges = async () => {
    if (!selectedRole) return;

    const selectedPermissionIds = selectedPermissions
      .filter((code) => !HIDDEN_PERMISSION_CODES.has(code))
      .map((code) => permissionByCode[code]?.id)
      .filter((id) => Number.isInteger(id));

    setIsSavingPermissions(true);
    try {
      const payload = {
        permissions: [...new Set(selectedPermissionIds)],
      };
      if (scope === "platform") {
        await savePlatformRolePermissions(selectedRole, payload);
      } else {
        await saveRolePermissions(targetIdForScope, selectedRole, payload);
      }
      setSaveResponseModal({
        show: true,
        type: "success",
        title: "Permissions Updated",
        message: "Permission Updated sucessfully.",
      });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error?.message ||
        "Unable to save permissions. Please try again.";
      setSaveResponseModal({
        show: true,
        type: "error",
        title: "Unable to Update Permissions",
        message: errorMessage,
      });
    } finally {
      setIsSavingPermissions(false);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onHide}
        centered
        size="xl"
        backdrop="static"
        dialogClassName={`${styles.dialog} ${
          showUserGuide ? styles.dialogBlurred : ""
        }`}
      >
      <Modal.Header closeButton className={styles.header}>
        <Modal.Title className={styles.title}>
          <span> Manage Roles & Permissions</span>
          <InfoCircleFill
            cursor="pointer"
            onClick={() => setShowUserGuide(true)}
            aria-label="Open roles and permissions guide"
            title="User roles description"
          />
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className={styles.body}>
        {!isClientAccount && (
          <section className={styles.section}>
            <p className={styles.sectionLabel}>CUSTOMIZE FOR</p>
            <div className={styles.radioGroup}>
              {isSystemAdmin && (
                <Form.Check
                  type="radio"
                  id="scope-platform"
                  label="Platform"
                  checked={scope === "platform"}
                  onChange={() => handleScopeChange("platform")}
                  name="scope"
                />
              )}
              <Form.Check
                type="radio"
                id="scope-entity"
                label="Entity"
                checked={scope === "entity"}
                onChange={() => handleScopeChange("entity")}
                name="scope"
              />
              <Form.Check
                type="radio"
                id="scope-client"
                label="Client"
                checked={scope === "client"}
                onChange={() => handleScopeChange("client")}
                name="scope"
              />
            </div>
          </section>
        )}

        <Row className="g-3 mt-1">
          {!isEntityAccount && !isClientAccount && scope !== "platform" && (
            <Col md={scope === "client" ? 4 : 6}>
              <section className={styles.section}>
                <p className={styles.sectionLabel}>SELECT ENTITY</p>
                <Form.Select
                  value={selectedEntity}
                  onChange={handleEntityChange}
                >
                  <option value="">
                    {isEntityLoading ? "Loading entities..." : "Select Entity"}
                  </option>
                  {entityOptions.map((entity) => (
                    <option key={entity.value} value={entity.value}>
                      {entity.label}
                    </option>
                  ))}
                </Form.Select>
              </section>
            </Col>
          )}

          {scope === "client" && !isClientAccount && (
            <Col md={4}>
              <section className={styles.section}>
                <p className={styles.sectionLabel}>SELECT CLIENT</p>
                <Form.Select
                  value={selectedClient}
                  onChange={(event) => {
                    setSelectedClient(event.target.value);
                    setSelectedRole("");
                  }}
                  disabled={!selectedEntity}
                >
                  <option value="">
                    {!selectedEntity
                      ? "Select Entity first"
                      : isClientLoading
                        ? "Loading clients..."
                        : "Select Client"}
                  </option>
                  {clientOptions.map((client) => (
                    <option key={client.value} value={client.value}>
                      {client.label}
                    </option>
                  ))}
                </Form.Select>
              </section>
            </Col>
          )}

          <Col md={isClientAccount ? 12 : scope === "client" ? 4 : 6}>
            <section className={styles.section}>
              <p className={styles.sectionLabel}>SELECT ROLE</p>
              {isRoleLoading ? (
                <div className={styles.permissionPlaceholder}>
                  Loading roles...
                </div>
              ) : roleOptions.length > 0 ? (
                <div className={styles.rolePills}>
                  {roleOptions.map((role) => (
                    <button
                      key={role.value}
                      type="button"
                      className={`${styles.rolePill} ${selectedRole === role.value ? styles.rolePillActive : ""}`}
                      disabled={isRoleDisabled}
                      onClick={() => setSelectedRole(role.value)}
                    >
                      {role.label}
                    </button>
                  ))}
                </div>
              ) : (
                <div className={styles.permissionPlaceholder}>
                  {scope === "entity"
                    ? selectedEntity
                      ? "No roles available for this entity."
                      : "Select an entity to load roles."
                    : selectedEntity && selectedClient
                      ? "No roles available for this client."
                      : "Select entity and client to load roles."}
                </div>
              )}
            </section>
          </Col>
        </Row>
        <section className={`${styles.section} ${styles.permissionsSection}`}>
          <p className={styles.sectionLabel}>PERMISSION CONFIGURATION</p>
          {!selectedRole ? (
            <div className={styles.permissionPlaceholder}>
              Select a role to configure permissions.
            </div>
          ) : isPermissionLoading ? (
            <div className={styles.permissionPlaceholder}>
              Loading permissions...
            </div>
          ) : (
            <>
              <div className={styles.permissionHeaderRow}>
                <Form.Control
                  size="sm"
                  placeholder="Search permissions (label, code, module)..."
                  value={permissionSearch}
                  onChange={(event) => setPermissionSearch(event.target.value)}
                  className={styles.permissionSearch}
                />
                <span className={styles.permissionAssignedCount}>
                  Assigned: {visibleAssignedCount}/{visibleTotalPermissions}
                </span>
              </div>
              <ViewOnlyWrapper viewOnly={isViewOnly}>
                <div className={styles.permissionListWrap}>
                  {filteredCategories.map((category) => {
                    const categoryCodes = category.subgroups.flatMap(
                      (subgroup) =>
                        subgroup.permissions
                          .map((perm) => perm.code)
                          .filter((code) => !lockedPermissionCodes.has(code)),
                    );
                    const categoryStats = getCheckedStats(categoryCodes);
                    const isOpen = openCategories[category.key] ?? true;
                    return (
                      <div
                        key={category.key}
                        className={styles.permissionCategory}
                      >
                        <button
                          type="button"
                          className={styles.permissionCategoryHead}
                          onClick={() => toggleCategory(category.key)}
                        >
                          <div>
                            <span className={styles.permissionCategoryChevron}>
                              {isOpen ? "▼" : "▶"}
                            </span>
                            <span className={styles.permissionCategoryTitle}>
                              {category.title}
                            </span>
                            <span className={styles.permissionCategoryMeta}>
                              {` ${categoryStats.checkedCount}/${categoryStats.total} | ${category.subgroups.length} subgroup${category.subgroups.length > 1 ? "s" : ""}`}
                            </span>
                          </div>
                          <div onClick={(event) => event.stopPropagation()}>
                            <Form.Check
                              type="checkbox"
                              label="Select all"
                              disabled={categoryStats.total === 0}
                              checked={
                                categoryStats.total > 0 &&
                                categoryStats.checkedCount ===
                                  categoryStats.total
                              }
                              onChange={(event) =>
                                handleBulkToggle(
                                  categoryCodes,
                                  event.target.checked,
                                )
                              }
                            />
                          </div>
                        </button>

                        {isOpen &&
                          category.subgroups.map((subgroup) => {
                            return (
                              <div
                                key={subgroup.key}
                                className={styles.permissionSubgroup}
                              >
                                <div className={styles.permissionRow}>
                                  <div className={styles.permissionRowTitle}>
                                    <span className={styles.permissionRowIcon}>
                                      {MODULE_ICONS[subgroup.moduleKey] ||
                                        MODULE_ICONS.system}
                                    </span>
                                    <strong>{subgroup.moduleLabel}</strong>
                                  </div>

                                  <div className={styles.permissionItems}>
                                    {subgroup.permissions.map((perm) => (
                                      <Form.Check
                                        key={perm.code}
                                        inline
                                        type="checkbox"
                                        id={`perm-${perm.code}`}
                                        label={derivePermissionAction(perm)}
                                        checked={selectedPermissions.includes(
                                          perm.code,
                                        )}
                                        disabled={lockedPermissionCodes.has(
                                          perm.code,
                                        )}
                                        onChange={(event) =>
                                          handlePermissionToggle(
                                            perm.code,
                                            event.target.checked,
                                          )
                                        }
                                        className={`${styles.permissionItem} ${
                                          lockedPermissionCodes.has(perm.code)
                                            ? styles.permissionItemLocked
                                            : ""
                                        }`}
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                      </div>
                    );
                  })}

                  {filteredCategories.length === 0 && (
                    <div className={styles.permissionPlaceholder}>
                      No permissions found for this search.
                    </div>
                  )}
                </div>
              </ViewOnlyWrapper>
            </>
          )}
        </section>
      </Modal.Body>

      <Modal.Footer className={styles.footer}>
        <Button variant="light" className="border" onClick={onHide}>
          Cancel
        </Button>
        {canManageRoles && (
          <Button
            variant="primary"
            disabled={!selectedRole || isSavingPermissions || isViewOnly}
            onClick={handleSaveChanges}
          >
            {isSavingPermissions ? "Saving..." : "Save Changes"}
          </Button>
        )}
      </Modal.Footer>

      <Modal
        show={dependencyModal.show}
        onHide={closeDependencyModal}
        centered
        dialogClassName={styles.dependencyDialog}
      >
        <Modal.Body className={styles.dependencyModalBody}>
          <button
            type="button"
            className={styles.dependencyClose}
            onClick={closeDependencyModal}
            aria-label="Close"
          >
            ×
          </button>
          <div className={styles.dependencyIconWrap} aria-hidden="true">
            !
          </div>
          <h5 className={styles.dependencyTitle}>
            Some Permissions are Locked
          </h5>
          <p className={styles.dependencyMessage}>
            These permissions are important for certain features to work
            properly and cannot be turned off. For example,{" "}
            <strong>{dependencyModal.parentLabel}</strong> relies on{" "}
            <strong>{dependencyModal.dependencyLabel}</strong> to work properly.
          </p>
          <Button
            variant="primary"
            className={styles.dependencyOkButton}
            onClick={closeDependencyModal}
          >
            OK
          </Button>
        </Modal.Body>
      </Modal>

      <Modal
        show={saveResponseModal.show}
        onHide={closeSaveResponseModal}
        centered
        dialogClassName={styles.saveResponseDialog}
      >
        <Modal.Body className={styles.saveResponseBody}>
          <button
            type="button"
            className={styles.saveResponseClose}
            onClick={closeSaveResponseModal}
            aria-label="Close"
          >
            ×
          </button>
          <div
            className={`${styles.saveResponseIcon} ${
              saveResponseModal.type === "success"
                ? styles.saveResponseIconSuccess
                : styles.saveResponseIconError
            }`}
            aria-hidden="true"
          >
            {saveResponseModal.type === "success" ? "✓" : "!"}
          </div>
          <h5 className={styles.saveResponseTitle}>
            {saveResponseModal.title}
          </h5>
          <p className={styles.saveResponseMessage}>
            {saveResponseModal.message}
          </p>
          <Button
            variant="primary"
            className={styles.dependencyOkButton}
            onClick={closeSaveResponseModal}
          >
            OK
          </Button>
        </Modal.Body>
      </Modal>
      </Modal>
      <UserGuideRolePermissions
        show={showUserGuide}
        onHide={() => setShowUserGuide(false)}
      />
    </>
  );
}

export default ManageRolesPermissions;
